import { supabase } from '../../lib/supabase';
import type {
  ResultBlock,
  ResultReviewItem,
  ResultWritingTask,
} from '../../shared/results/result-types';
import type {
  ResultBands,
  ResultComments,
  ResultItemFeedback,
  ResultSpeaking,
} from '../../shared/results/ResultDetail';

// `rpcUnknown` escape hatch: the Phase 80 RPCs aren't in the generated types
// until the user re-runs `gen types`, so we narrow through `unknown` to keep
// this file typecheck-clean (same pattern as institute/grading-api.ts).
interface UnknownRpc {
  rpc: (
    fn: string,
    args?: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: { message: string } | null }>;
}
const rpcUnknown = supabase as unknown as UnknownRpc;

export type ResultAttemptStatus = 'submitted' | 'grading' | 'completed';

/** One row in the institute-wide results browser. */
export interface InstituteResultRow {
  attempt_id: string;
  student_id: string;
  student_name: string;
  test_title: string;
  exam_type: 'academic' | 'general';
  kind: 'scheduled' | 'practice' | 'assignment';
  /** Phase 161: a question-type drill, not a mock. Absent before that
   *  migration — read it as `=== true`. */
  is_drill?: boolean;
  session_id: string | null;
  started_at: string;
  submitted_at: string | null;
  status: ResultAttemptStatus;
  listening_band: number | null;
  reading_band: number | null;
  writing_band: number | null;
  speaking_band: number | null;
  overall_band: number | null;
  published_at: string | null;
  /** Phase 194 — every section kind in the test, in paper order. Absent on a
   *  payload from before that migration, in which case the browser hides its
   *  skill chips rather than filtering on nothing. */
  sections?: string[] | null;
}

/** Detailed result for one attempt — the student's shape plus staff context. */
export interface InstituteAttemptResult {
  /** Phase 175 — links the attempt page back to the dossier. Absent on a
   *  payload from before that migration. */
  student_id?: string;
  student_name: string;
  test_title: string;
  result: ResultBands | null;
  review: ResultReviewItem[];
  /** Phase 188 — the shared stimulus behind the review's block blanks. Absent
   *  on a payload from before that migration. */
  blocks?: ResultBlock[];
  items: ResultItemFeedback[];
  comments: ResultComments;
  /** Phase 175 — essay + examiner criteria per task; recording availability +
   *  examiner criteria per part. Absent before that migration. */
  writing?: ResultWritingTask[];
  speaking?: ResultSpeaking | null;
}

export const INSTITUTE_RESULTS_KEYS = {
  list: (instituteId: string) => ['institute', 'results', instituteId] as const,
  detail: (attemptId: string) => ['institute', 'result_detail', attemptId] as const,
};

export async function getInstituteResults(instituteId: string): Promise<InstituteResultRow[]> {
  const { data, error } = await rpcUnknown.rpc('institute_view_results', {
    p_institute_id: instituteId,
  });
  if (error !== null) throw new Error(error.message);
  return (data as InstituteResultRow[] | null) ?? [];
}

export async function getInstituteAttemptResult(
  attemptId: string,
): Promise<InstituteAttemptResult> {
  const { data, error } = await rpcUnknown.rpc('institute_view_attempt_result', {
    p_attempt_id: attemptId,
  });
  if (error !== null) throw new Error(error.message);
  if (data === null || typeof data !== 'object') throw new Error('no result');
  return data as InstituteAttemptResult;
}
